import { PHASES } from "../data/phases";
import type {
  CycleInfo,
  CyclePhaseInfo,
  CycleRegularity,
  KayaState,
} from "./types";
import { DAY_MS, addDays, parseDay, todayStr } from "./date";

/** Every cycle feature asks this. "Periods have stopped" answers no even beside a stale
 *  `cycleOn`, so the logged periods stay on file but predict nothing. (ADR-039) */
export function cycleOn(S: KayaState): boolean {
  return !!S.profile.cycleOn && S.profile.periodsStopped !== true;
}

/** Where `ds` falls in the cycle, or null when tracking is off. */
export function cycleInfo(S: KayaState, ds?: string): CycleInfo | null {
  const day0 = ds || todayStr();
  if (!cycleOn(S)) return null;

  const starts = (S.cycle.starts || []).slice().sort();
  const d = parseDay(day0);
  let last: string | null = null;
  for (const st of starts) if (parseDay(st) <= d) last = st;

  const L = S.cycle.length || 28;
  const PL = S.cycle.periodLen || 5;
  if (!last)
    return { day: null, inPeriod: false, predicted: false, next: null, daysToNext: null, pms: false };

  const day = Math.floor((d.getTime() - parseDay(last).getTime()) / DAY_MS) + 1;
  const ended = S.cycle.ends && S.cycle.ends[last];
  const endDay = ended
    ? Math.floor((parseDay(ended).getTime() - parseDay(last).getTime()) / DAY_MS) + 1
    : PL;
  const inPeriod = day >= 1 && day <= endDay;

  const nextStart = parseDay(last);
  while (nextStart <= d) nextStart.setDate(nextStart.getDate() + L);

  const cycDay = ((day - 1) % L) + 1;
  const predicted = !inPeriod && day > L && cycDay <= PL;
  const daysToNext = Math.round((nextStart.getTime() - d.getTime()) / DAY_MS);
  const pms = !inPeriod && !predicted && daysToNext <= 4;

  return { day: cycDay, inPeriod, predicted, next: todayStr(nextStart), daysToNext, pms, last };
}

export function periodDay(S: KayaState, ds?: string): boolean {
  const c = cycleInfo(S, ds);
  return !!(c && c.inPeriod);
}

/** Gap analysis over logged starts — drives the "cycles look irregular" note. */
export function cycleRegularity(S: KayaState, now: number = Date.now()): CycleRegularity {
  const starts = (S.cycle.starts || []).slice().sort();
  const gaps: number[] = [];
  for (let i = 1; i < starts.length; i++)
    gaps.push(Math.round((parseDay(starts[i]).getTime() - parseDay(starts[i - 1]).getTime()) / DAY_MS));

  const c = cycleInfo(S, todayStr(new Date(now)));
  const late =
    !!c &&
    c.day !== null &&
    c.daysToNext !== null &&
    (now - parseDay(c.last!).getTime()) / DAY_MS > (S.cycle.length || 28) + 10;
  const detected =
    gaps.length >= 2 &&
    (Math.max(...gaps) - Math.min(...gaps) >= 9 || gaps.some((g) => g < 21 || g > 35));
  const absent =
    !!starts.length && (now - parseDay(starts[starts.length - 1]).getTime()) / DAY_MS > 90;

  return {
    gaps,
    detected,
    late,
    absent,
    irregular: S.profile.irregular || detected,
    min: gaps.length ? Math.min(...gaps) : null,
    max: gaps.length ? Math.max(...gaps) : null,
  };
}

/** Which of the four phases `ds` sits in, plus the day ranges for the wheel. */
export function cyclePhase(S: KayaState, ds?: string): CyclePhaseInfo | null {
  const c = cycleInfo(S, ds);
  if (!c || c.day === null) return null;

  const L = S.cycle.length || 28;
  const PL = S.cycle.periodLen || 5;
  const ov = L - 14;
  const d = c.day;

  let id: CyclePhaseInfo["phase"]["id"];
  if (c.inPeriod || (d <= PL && !c.predicted)) id = "menstrual";
  else if (d < ov - 1) id = "follicular";
  else if (d <= ov + 1) id = "ovulation";
  else id = "luteal";

  return {
    phase: PHASES.find((p) => p.id === id)!,
    day: d,
    len: L,
    ranges: {
      menstrual: [1, PL],
      follicular: [PL + 1, ov - 2],
      ovulation: [ov - 1, ov + 1],
      luteal: [ov + 2, L],
    },
  };
}

/* ---------- estimates (ADR-033) ---------- */

/** On a hormonal method the bleed is a withdrawal bleed, not a natural cycle: no phase, no
 *  ovulation. Absent or "none" is not hormonal; "other" is the settings row's "other hormonal". */
export function hormonal(S: KayaState): boolean {
  const c = S.cycle.contraception;
  return !!c && c !== "none";
}

/** The predicted next start after `ds`, from the last logged start and the set length — the same
 *  date Today shows. Null with tracking off or nothing logged. */
export function nextPeriodEstimate(S: KayaState, ds: string): string | null {
  return cycleInfo(S, ds)?.next ?? null;
}

/**
 * Ovulation, estimated as 14 days before the predicted start. The luteal phase averages about 12
 * days and ranges 7–17, so this is only ever shown as "est.". Null on a hormonal method.
 */
export function ovulationEstimate(S: KayaState, ds: string): string | null {
  if (hormonal(S)) return null;
  const next = nextPeriodEstimate(S, ds);
  return next ? addDays(next, -14) : null;
}

/** The seven days before the predicted start — the pre-period week the plan eases into. */
export function prePeriodWindow(S: KayaState, ds: string): { from: string; to: string } | null {
  const next = nextPeriodEstimate(S, ds);
  return next ? { from: addDays(next, -7), to: addDays(next, -1) } : null;
}

/** Logged starts, then predicted ones after the last of them, up to and including `until`. */
function startsThrough(S: KayaState, until: string): string[] {
  const logged = (S.cycle.starts || []).slice().sort();
  if (!logged.length) return [];
  const out = logged.filter((st) => st <= until);
  const L = S.cycle.length || 28;
  let p = addDays(logged[logged.length - 1], L);
  // bounded: a start logged years ago must not spin here
  for (let i = 0; i < 60 && p <= until; i++, p = addDays(p, L)) out.push(p);
  return out;
}

/**
 * A day whose weigh-in or appetite the cycle can explain: the 5 days before a logged or predicted
 * start through day 3 of it. Readings in this window run high on water and are tagged, not
 * hidden (ADR-033 decision 5).
 */
export function cycleAffected(S: KayaState, ds: string): boolean {
  if (!cycleOn(S)) return false;
  return startsThrough(S, addDays(ds, 5)).some((st) => ds >= addDays(st, -5) && ds <= addDays(st, 2));
}

/** Inside the seven days before a logged or predicted start. */
export function prePeriodDay(S: KayaState, ds: string): boolean {
  if (!cycleOn(S)) return false;
  return startsThrough(S, addDays(ds, 7)).some((st) => ds >= addDays(st, -7) && ds < st);
}

export type PhaseId = CyclePhaseInfo["phase"]["id"];

/** The phase `ds` falls in, or null — tracking off, nothing logged, or a hormonal method. */
export function phaseOf(S: KayaState, ds: string): PhaseId | null {
  if (hormonal(S)) return null;
  return cyclePhase(S, ds)?.phase.id ?? null;
}
